import React, { useEffect, useRef, useState, useMemo } from 'react';
import { haptic } from '../utils/vibration';
import {
  playJingleBellsStep,
  playJingleBellsTune,
  stopJingleBellsTune,
  isJingleBellsPlaying,
  JINGLE_BELLS_STATE_CHANGED_EVENT,
  triggerHolidayBurst
} from '../utils/newYearInterface';
import '../styles/newYearInterface.css';

// Цвета для гирлянды на ПК
const GARLAND_COLORS = ['red', 'gold', 'green', 'blue', 'purple'];

// Снежинки для снегопада во время звучания Jingle Bells
const MUSIC_SNOWFLAKE_SYMBOLS = ['❄', '❅', '✧', '✦', '✻', '❄'];

const MUSIC_SNOWFLAKES = Array.from({ length: 22 }, (_, index) => {
  const sym = MUSIC_SNOWFLAKE_SYMBOLS[index % MUSIC_SNOWFLAKE_SYMBOLS.length];
  const size = 16 + ((index * 5) % 12);
  return {
    left: `${2 + ((index * 17) % 94)}%`,
    size: `${size}px`,
    delay: `${-((index * 0.7) % 10).toFixed(2)}s`,
    duration: `${7.5 + ((index * 3) % 14) / 10}s`,
    drift: `${-35 + ((index * 21) % 70)}px`,
    opacity: (0.6 + ((index * 9) % 35) / 100).toFixed(2),
    char: sym
  };
});

// --- Векторная новогодняя шапочка для левого верхнего угла аватара профиля ---
export const NewYearAvatarSantaHat = () => (
  <svg
    viewBox="0 0 42 40"
    className="new-year-avatar-santa-hat"
    aria-hidden="true"
    fill="none"
  >
    <defs>
      {/* Насыщенный праздничный красный градиент — сплошной глубокий красный цвет */}
      <linearGradient id="avatarSantaRed" x1="15%" y1="0%" x2="85%" y2="100%">
        <stop offset="0%" stopColor="#ff453a" />
        <stop offset="45%" stopColor="#e02424" />
        <stop offset="100%" stopColor="#b91c1c" />
      </linearGradient>
      {/* Мягкая тень под опушкой и помпончиком */}
      <filter id="avatarSantaFluffShadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="1" stdDeviation="0.8" floodColor="rgba(0,0,0,0.22)" />
      </filter>
    </defs>
    
    {/* Сплошной красный колпак (цельное плотное тело без просветов) */}
    <path
      d="M 7 26 C 6 15, 10 5, 20 3 C 28 2, 35 7, 37 15 C 38 19.5, 34 21, 31 19.5 C 28 16.5, 26 12, 22 12 C 23 17, 25 21, 27 26 Z"
      fill="url(#avatarSantaRed)"
    />

    {/* Естественная складка сгиба колпака для 3D глубины */}
    <path
      d="M 20 3 C 28 2, 35 7, 37 15 C 33 15.5, 29 13.5, 26 9.5 C 23 6.5, 21 4.5, 20 3 Z"
      fill="rgba(0,0,0,0.14)"
    />

    {/* Белая пушистая опушка по основанию колпака */}
    <rect
      x="4"
      y="22.5"
      width="26"
      height="7.5"
      rx="3.75"
      fill="#ffffff"
      stroke="#e2e8f0"
      strokeWidth="0.5"
      filter="url(#avatarSantaFluffShadow)"
    />

    {/* Белый помпончик на кончике колпака */}
    <circle
      cx="35"
      cy="19.5"
      r="5"
      fill="#ffffff"
      stroke="#e2e8f0"
      strokeWidth="0.5"
      filter="url(#avatarSantaFluffShadow)"
    />
    {/* Мягкий световой блик на помпончике */}
    <circle cx="33.8" cy="18" r="1.6" fill="#f8fafc" />
  </svg>
);

// --- Интерактивная ёлочка рядом с ФИО профиля ---
export const NewYearTreeButton = () => {
  const [isPlaying, setIsPlaying] = useState(() => isJingleBellsPlaying());

  useEffect(() => {
    const handleMusicState = (event) => {
      setIsPlaying(Boolean(event?.detail));
    };
    window.addEventListener(JINGLE_BELLS_STATE_CHANGED_EVENT, handleMusicState);
    return () => {
      window.removeEventListener(JINGLE_BELLS_STATE_CHANGED_EVENT, handleMusicState);
    };
  }, []);

  const handleClick = (e) => {
    e.stopPropagation(); // Не переходить на страницу профиля!
    haptic('selection');

    if (isJingleBellsPlaying()) {
      stopJingleBellsTune();
    } else {
      playJingleBellsTune();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`new-year-tree-btn ${isPlaying ? 'is-playing' : ''}`}
      aria-label={isPlaying ? 'Остановить новогоднюю музыку' : 'Включить новогоднюю музыку'}
      title={isPlaying ? 'Играет Jingle Bells (нажмите, чтобы остановить) 🔔' : 'Нажмите на ёлочку, чтобы пошёл снег и заиграла музыка 🎄'}
    >
      <span className="new-year-tree-icon" aria-hidden="true">🎄</span>
      {isPlaying && (
        <span className="new-year-tree-note" aria-hidden="true">♪</span>
      )}
    </button>
  );
};

// --- Снегопад во время звучания Jingle Bells (идёт по всему экрану и по сайдбару) ---
export const NewYearMusicSnowfall = ({ enabled = false }) => {
  const [isPlaying, setIsPlaying] = useState(() => isJingleBellsPlaying());

  useEffect(() => {
    const handleMusicState = (event) => {
      setIsPlaying(Boolean(event?.detail));
    };
    window.addEventListener(JINGLE_BELLS_STATE_CHANGED_EVENT, handleMusicState);
    return () => {
      window.removeEventListener(JINGLE_BELLS_STATE_CHANGED_EVENT, handleMusicState);
    };
  }, []);

  if (!enabled || !isPlaying) return null;

  return (
    <div className="new-year-music-snowfall" aria-hidden="true">
      {MUSIC_SNOWFLAKES.map((flake, index) => (
        <span
          key={index}
          className="new-year-music-flake"
          style={{
            left: flake.left,
            fontSize: flake.size,
            opacity: flake.opacity,
            '--flake-delay': flake.delay,
            '--flake-duration': flake.duration,
            '--ambient-drift': flake.drift
          }}
        >
          {flake.char}
        </span>
      ))}
    </div>
  );
};

// --- Чистая брендовая плашка сайдбара ---
export const NewYearSidebarBrand = ({ enabled = false }) => (
  <div className="new-year-sidebar-brand min-w-0">
    <div className="new-year-brand-wrapper">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-base font-bold text-transparent sm:text-lg lg:text-xl truncate">
        Большая Стирка
      </div>
    </div>
  </div>
);

// --- Новогодняя гирлянда для ПК версии (>=640px) ---
export const NewYearGarland = ({ enabled = false }) => {
  const [activeBulb, setActiveBulb] = useState(null);
  const swingTimerRef = useRef(null);

  const bulbs = useMemo(() => {
    return Array.from({ length: 18 }, (_, index) => ({
      id: index,
      color: GARLAND_COLORS[index % GARLAND_COLORS.length],
      delay: `${((index * 0.23) % 2.5).toFixed(2)}s`
    }));
  }, []);

  if (!enabled) return null;

  const handleBulbClick = (index, color, event) => {
    event.stopPropagation();
    haptic('selection');
    playJingleBellsStep();

    if (event?.clientX && event?.clientY) {
      triggerHolidayBurst(event.clientX, event.clientY);
    }

    if (swingTimerRef.current) window.clearTimeout(swingTimerRef.current);
    setActiveBulb(index);
    swingTimerRef.current = window.setTimeout(() => {
      setActiveBulb(null);
      swingTimerRef.current = null;
    }, 850);
  };

  return (
    <div className="new-year-garland hidden sm:block" aria-hidden="true">
      <svg className="new-year-garland-wire-svg" viewBox="0 0 1000 12" preserveAspectRatio="none">
        <path d="M 0,2 Q 50,11 100,2 T 200,2 T 300,2 T 400,2 T 500,2 T 600,2 T 700,2 T 800,2 T 900,2 T 1000,2" />
      </svg>
      <div className="new-year-garland-bulbs">
        {bulbs.map((bulb) => (
          <button
            key={bulb.id}
            type="button"
            className={`new-year-garland-bulb-btn ${
              activeBulb === bulb.id ? 'is-swinging' : ''
            }`}
            onClick={(e) => handleBulbClick(bulb.id, bulb.color, e)}
            aria-label="Лампочка гирлянды — нажми для музыки"
            title="Сыграть ноту Jingle Bells 🔔"
          >
            <span className="new-year-garland-socket" />
            <span
              className={`new-year-garland-bulb new-year-garland-bulb--${bulb.color}`}
              style={{ animationDelay: bulb.delay }}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

// Заглушки для обратной совместимости
export const NewYearSidebarSnowfall = () => null;
export const NewYearAmbientSnowfall = () => null;
